import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Home as HomeIcon, Wallet, Banknote, CalendarCheck, CheckCircle2,
  Clock, XCircle, AlertTriangle, ArrowRightLeft, Calendar, Phone, MessageCircle,
  MapPin, Fuel, ShieldAlert, Plus, Loader2, RefreshCw, Filter, UserCheck,
  Stethoscope, HandCoins, Search, ChevronRight, User
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from "recharts";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, fmtDate, telLink, waLink, mapsLink, todayIST, initials } from "@/lib/format";
import { PageHeader, StatCard, Loading, AccountBalanceBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const COLORS = ["#27AE60", "#1565C0", "#F59E0B", "#EF4444", "#8B5CF6", "#64748B"];

export default function HomeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dateRangeType, setDateRangeType] = useState("today"); // today | yesterday | this_week | this_month | custom
  const [dateFrom, setDateFrom] = useState(todayIST());
  const [dateTo, setDateTo] = useState(todayIST());
  const [selectedPhysio, setSelectedPhysio] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [patientSearch, setPatientSearch] = useState("");

  const [physios, setPhysios] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Transfer modal
  const [transferOpen, setTransferOpen] = useState(false);
  const [selectedPatientForTransfer, setSelectedPatientForTransfer] = useState(null);
  const [newPhysioId, setNewPhysioId] = useState("");
  const [transferDate, setTransferDate] = useState(todayIST());
  const [transferReason, setTransferReason] = useState("");
  const [transferNote, setTransferNote] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);

  // Hold / Availability modal
  const [holdOpen, setHoldOpen] = useState(false);
  const [selectedPatientForHold, setSelectedPatientForHold] = useState(null);
  const [holdFrom, setHoldFrom] = useState(todayIST());
  const [holdUntil, setHoldUntil] = useState("");
  const [holdReason, setHoldReason] = useState("");
  const [holdNextVisit, setHoldNextVisit] = useState("");
  const [holdNotes, setHoldNotes] = useState("");
  const [holdLoading, setHoldLoading] = useState(false);

  // Schedule modal
  const [schedOpen, setSchedOpen] = useState(false);
  const [selectedPatientForSched, setSelectedPatientForSched] = useState(null);
  const [schedDate, setSchedDate] = useState(todayIST());
  const [schedSlot, setSchedSlot] = useState("");
  const [schedNotes, setSchedNotes] = useState("");
  const [schedLoading, setSchedLoading] = useState(false);

  // Load physiotherapists list
  const loadPhysios = useCallback(async () => {
    if (user?.role === "physiotherapist") return;
    try {
      const res = await api.get("/users/physiotherapists");
      if (Array.isArray(res.data) && res.data.length > 0) {
        setPhysios(res.data);
      }
    } catch (e) {
      try {
        const res2 = await api.get("/users", { params: { role: "physiotherapist" } });
        if (Array.isArray(res2.data)) {
          setPhysios(res2.data);
        }
      } catch (err) {}
    }
  }, [user]);

  useEffect(() => {
    loadPhysios();
  }, [loadPhysios]);

  // Set date ranges
  const applyRange = (type) => {
    setDateRangeType(type);
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);

    if (type === "today") {
      const t = todayIST();
      setDateFrom(t);
      setDateTo(t);
    } else if (type === "yesterday") {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      const yStr = iso(y);
      setDateFrom(yStr);
      setDateTo(yStr);
    } else if (type === "this_week") {
      const curr = new Date(today);
      const first = curr.getDate() - curr.getDay() + (curr.getDay() === 0 ? -6 : 1);
      const firstDay = new Date(curr.setDate(first));
      setDateFrom(iso(firstDay));
      setDateTo(todayIST());
    } else if (type === "this_month") {
      const mStr = todayIST().slice(0, 7);
      setDateFrom(`${mStr}-01`);
      setDateTo(todayIST());
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: dateFrom,
        date_to: dateTo,
      };
      if (selectedPhysio && selectedPhysio !== "all") {
        params.physio_id = selectedPhysio;
      }
      if (selectedStatus && selectedStatus !== "all") {
        params.status = selectedStatus;
      }
      const res = await api.get("/dashboard/home", { params });
      setData(res.data);
      if (res.data?.physios && res.data.physios.length > 0 && physios.length === 0) {
        setPhysios(res.data.physios);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedPhysio, selectedStatus, physios.length]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Transfer submit
  const handleTransfer = async () => {
    if (!newPhysioId) {
      toast.error("Please select a target physiotherapist");
      return;
    }
    if (!transferReason || transferReason.trim().length < 3) {
      toast.error("Transfer reason is mandatory");
      return;
    }
    setTransferLoading(true);
    try {
      await api.post("/home-visits/transfer-patient", {
        patient_id: selectedPatientForTransfer.id,
        new_physio_id: newPhysioId,
        effective_date: transferDate,
        reason: transferReason.trim(),
        handover_note: transferNote,
      });
      toast.success("Patient transferred successfully");
      setTransferOpen(false);
      loadData();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTransferLoading(false);
    }
  };

  // Hold submit
  const handleHold = async () => {
    if (!holdFrom || !holdUntil) {
      toast.error("Please provide from and until dates");
      return;
    }
    if (!holdReason || holdReason.trim().length < 3) {
      toast.error("Hold reason is mandatory");
      return;
    }
    setHoldLoading(true);
    try {
      await api.post(`/home-visits/patients/${selectedPatientForHold.id}/home-availability`, {
        unavailable_from: holdFrom,
        unavailable_until: holdUntil,
        reason: holdReason.trim(),
        next_visit_date: holdNextVisit || null,
        notes: holdNotes,
      });
      toast.success("Home hold saved");
      setHoldOpen(false);
      loadData();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setHoldLoading(false);
    }
  };

  // Clear hold
  const handleClearHold = async (pat) => {
    try {
      await api.post(`/home-visits/patients/${pat.id}/clear-home-availability`);
      toast.success("Home hold cleared");
      loadData();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Schedule visit submit
  const handleScheduleVisit = async () => {
    if (!schedDate) {
      toast.error("Please select a visit date");
      return;
    }
    setSchedLoading(true);
    try {
      await api.post("/home-visits", {
        patient_id: selectedPatientForSched.id,
        date: schedDate,
        time_slot: schedSlot,
        notes: schedNotes,
      });
      toast.success("Home visit scheduled");
      setSchedOpen(false);
      loadData();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSchedLoading(false);
    }
  };

  const kpis = data?.kpis || {};
  const alerts = data?.alerts || [];
  const rawPatients = data?.patients || [];
  const charts = data?.charts || {};

  // Filter patients by search query
  const filteredPatients = useMemo(() => {
    const list = data?.patients || [];
    if (!patientSearch) return list;
    const q = patientSearch.toLowerCase();
    return list.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.patient_id?.toLowerCase().includes(q) ||
        p.phone?.includes(q) ||
        p.locality?.toLowerCase().includes(q) ||
        p.assigned_physio_name?.toLowerCase().includes(q)
    );
  }, [data?.patients, patientSearch]);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Home Service Dashboard"
        subtitle="Multi-physiotherapist home visits, attribution, deposits, and financial operations"
        testid="home-dashboard"
        action={
          <Button
            size="sm"
            onClick={loadData}
            variant="outline"
            className="gap-1.5"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      {/* Filter Bar */}
      <Card className="p-4 bg-slate-50/80 border-slate-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* Quick Date Range Buttons */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 mr-1 flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" /> Date:
            </span>
            <Button
              size="sm"
              variant={dateRangeType === "today" ? "default" : "outline"}
              onClick={() => applyRange("today")}
              className={`h-8 text-xs ${dateRangeType === "today" ? "bg-bk-blue text-white" : "bg-white"}`}
            >
              Today
            </Button>
            <Button
              size="sm"
              variant={dateRangeType === "yesterday" ? "default" : "outline"}
              onClick={() => applyRange("yesterday")}
              className={`h-8 text-xs ${dateRangeType === "yesterday" ? "bg-bk-blue text-white" : "bg-white"}`}
            >
              Yesterday
            </Button>
            <Button
              size="sm"
              variant={dateRangeType === "this_week" ? "default" : "outline"}
              onClick={() => applyRange("this_week")}
              className={`h-8 text-xs ${dateRangeType === "this_week" ? "bg-bk-blue text-white" : "bg-white"}`}
            >
              This Week
            </Button>
            <Button
              size="sm"
              variant={dateRangeType === "this_month" ? "default" : "outline"}
              onClick={() => applyRange("this_month")}
              className={`h-8 text-xs ${dateRangeType === "this_month" ? "bg-bk-blue text-white" : "bg-white"}`}
            >
              This Month
            </Button>
          </div>

          {/* Custom Date Pickers & Physio Selector */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setDateRangeType("custom");
                }}
                className="h-8 text-xs w-32 bg-white"
              />
              <span className="text-xs text-slate-400">to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setDateRangeType("custom");
                }}
                className="h-8 text-xs w-32 bg-white"
              />
            </div>

            {user?.role !== "physiotherapist" && (
              <Select value={selectedPhysio} onValueChange={setSelectedPhysio}>
                <SelectTrigger className="h-8 text-xs w-48 bg-white font-medium">
                  <SelectValue placeholder="All Physiotherapists" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Physiotherapists ({physios.length})</SelectItem>
                  {physios.map((ph) => (
                    <SelectItem key={ph.id} value={ph.id}>
                      {ph.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="h-8 text-xs w-36 bg-white">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="started">Started</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="patient_unavailable">Patient Unavailable</SelectItem>
                <SelectItem value="patient_cancelled">Patient Cancelled</SelectItem>
                <SelectItem value="provider_cancelled">Provider Cancelled</SelectItem>
                <SelectItem value="rescheduled">Rescheduled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Conflict & Workload Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, idx) => (
            <div
              key={idx}
              className={`p-3 rounded-lg border flex items-center justify-between gap-3 text-xs ${
                alert.severity === "danger"
                  ? "bg-red-50 text-red-900 border-red-200"
                  : "bg-amber-50 text-amber-900 border-amber-200"
              }`}
            >
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 shrink-0 text-red-600" />
                <span className="font-medium">{alert.message}</span>
              </div>
              {alert.visit_id && user?.role === "admin" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate("/home-visits")}
                  className="h-7 text-[11px] bg-white"
                >
                  Manage Visit
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <Loading label="Computing home dashboard metrics..." />
      ) : (
        <>
          {/* Main KPI Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
            <StatCard
              testid="kpi-expected-visits"
              label="Expected Visits"
              value={kpis.expected_visits || 0}
              icon={CalendarCheck}
              tone="blue"
              onClick={() => navigate("/home-visits")}
            />
            <StatCard
              testid="kpi-completed-visits"
              label="Completed Visits"
              value={kpis.completed || 0}
              icon={CheckCircle2}
              tone="green"
              sub={`Started: ${kpis.started || 0}`}
            />
            <StatCard
              testid="kpi-unavail-cancelled"
              label="Unavailable / Cancelled"
              value={(kpis.patient_unavailable || 0) + (kpis.patient_cancelled || 0) + (kpis.provider_cancelled || 0)}
              icon={XCircle}
              tone="danger"
              sub={`Provider Cancel: ${kpis.provider_cancelled || 0} · Resched: ${kpis.rescheduled || 0}`}
            />
            <StatCard
              testid="kpi-total-collection"
              label="Total Collection"
              value={inr(kpis.total_collection || 0)}
              icon={Wallet}
              tone="green"
              sub={`Physio: ${inr(kpis.physio_collection || 0)} · Owner: ${inr(kpis.owner_collection || 0)}`}
            />
            <StatCard
              testid="kpi-service-revenue"
              label="Home Service Value"
              value={inr(kpis.home_service_revenue || 0)}
              icon={Stethoscope}
              tone="blue"
              sub="Completed session charges"
            />
            <StatCard
              testid="kpi-pending-deposits"
              label="Pending Physio Deposits"
              value={inr(kpis.pending_physio_deposits || 0)}
              icon={Banknote}
              tone="danger"
              sub={`Submitted awaiting verify: ${inr(kpis.submitted_deposits || 0)}`}
              onClick={() => navigate("/deposits")}
            />
            <StatCard
              testid="kpi-approved-petrol"
              label="Approved Travel Expense"
              value={inr(kpis.approved_petrol_expense || 0)}
              icon={Fuel}
              tone="slate"
              onClick={() => navigate("/expenses")}
            />
            <StatCard
              testid="kpi-outstanding-due"
              label="Outstanding Patient Due"
              value={inr(kpis.total_outstanding_due || 0)}
              icon={AlertTriangle}
              tone="danger"
              sub="Receivable from home patients"
            />
          </div>

          {/* Charts Section */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-4">
              <p className="font-display font-bold text-slate-800 text-sm mb-3">
                Daily Completed Home Visits
              </p>
              <div className="h-60">
                {charts.daily_trend?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.daily_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={30} allowDecimals={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="completed" fill="#27AE60" name="Completed" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="cancelled" fill="#EF4444" name="Cancelled" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-xs text-slate-400">
                    No visit trend data in selected range
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-4">
              <p className="font-display font-bold text-slate-800 text-sm mb-3">
                Collections: Physio Collected vs Owner Direct
              </p>
              <div className="h-60">
                {charts.owner_vs_physio?.some((x) => x.value > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={charts.owner_vs_physio}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={75}
                        label={(e) => `${e.name}: ${inr(e.value)}`}
                      >
                        {charts.owner_vs_physio.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => inr(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-xs text-slate-400">
                    No collections in selected range
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Active Home Patients Cards Section */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white p-4 rounded-xl border border-slate-200">
              <div>
                <h3 className="font-display font-bold text-slate-900 text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-bk-blue" />
                  Active Home Patients ({filteredPatients.length})
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Assigned physiotherapists, visit counts, financial status, and direct management shortcuts
                </p>
              </div>

              {/* Patient Quick Search Input */}
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search patient, phone, locality..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  className="pl-8 h-9 text-xs bg-slate-50"
                />
              </div>
            </div>

            {filteredPatients.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No active home patients found"
                subtitle="Try adjusting your search or selecting 'All Physiotherapists'."
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredPatients.map((pat) => {
                  const isHold = Boolean(pat.home_unavailable_until);
                  const isAssigned = pat.assigned_physio_name && pat.assigned_physio_name !== "Unassigned";

                  return (
                    <Card
                      key={pat.id}
                      className="p-4 hover:shadow-md transition-all border-slate-200 flex flex-col justify-between bg-white relative overflow-hidden group"
                    >
                      {/* Top Accent bar */}
                      <div
                        className={`absolute top-0 left-0 right-0 h-1 ${
                          isHold
                            ? "bg-amber-500"
                            : pat.account_balance < 0
                            ? "bg-red-500"
                            : "bg-emerald-500"
                        }`}
                      />

                      <div className="space-y-3 pt-1">
                        {/* Header: Avatar, Name, Code, Locality */}
                        <div className="flex items-start justify-between gap-2.5">
                          <div className="flex items-start gap-2.5 min-w-0">
                            <Avatar
                              className="h-11 w-11 cursor-pointer ring-2 ring-slate-100 hover:ring-bk-blue transition-all shrink-0"
                              onClick={() => navigate(`/patients/${pat.id}`)}
                            >
                              <AvatarFallback className="bg-bk-lightGreen text-bk-green font-bold text-sm">
                                {initials(pat.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <button
                                onClick={() => navigate(`/patients/${pat.id}`)}
                                className="font-bold text-slate-900 text-sm hover:text-bk-blue text-left truncate block max-w-[180px] leading-tight"
                              >
                                {pat.name}
                              </button>
                              <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                                {pat.patient_id}
                              </p>
                              {pat.locality && (
                                <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5 truncate">
                                  <MapPin className="h-3 w-3 text-slate-400 shrink-0" />
                                  <span className="truncate">{pat.locality}</span>
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Status Badge */}
                          <div className="shrink-0 flex flex-col items-end gap-1">
                            {isHold ? (
                              <Badge
                                variant="outline"
                                className="bg-amber-50 text-amber-800 border-amber-200 text-[10px] px-2 py-0.5 font-medium"
                              >
                                <Clock className="h-3 w-3 mr-1" /> Hold
                              </Badge>
                            ) : pat.next_visit_date ? (
                              <Badge
                                variant="outline"
                                className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] px-2 py-0.5"
                              >
                                Next: {fmtDate(pat.next_visit_date)}
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] px-2 py-0.5"
                              >
                                Active
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Assigned Physiotherapist Pill */}
                        <div className="p-2 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="h-6 w-6 rounded-full bg-blue-100 text-bk-blue flex items-center justify-center shrink-0">
                              <Stethoscope className="h-3.5 w-3.5" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-[10px] uppercase font-semibold text-slate-400 leading-none">
                                Assigned Physiotherapist
                              </p>
                              <p className={`text-xs font-bold truncate mt-0.5 ${isAssigned ? "text-slate-800" : "text-amber-600 italic"}`}>
                                {pat.assigned_physio_name || "Unassigned"}
                              </p>
                            </div>
                          </div>

                          {user?.role === "admin" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedPatientForTransfer(pat);
                                setNewPhysioId("");
                                setTransferDate(todayIST());
                                setTransferReason("");
                                setTransferNote("");
                                setTransferOpen(true);
                              }}
                              className="h-6 px-2 text-[11px] text-bk-blue hover:bg-blue-100 hover:text-blue-800"
                              title="Transfer Patient"
                            >
                              <ArrowRightLeft className="h-3 w-3 mr-1" /> Transfer
                            </Button>
                          )}
                        </div>

                        {/* 3-Column Metrics: Balance, Period Visits, Period Collection */}
                        <div className="grid grid-cols-3 gap-1.5 p-2 bg-slate-50/70 border rounded-lg text-center">
                          <div>
                            <p className="text-[10px] uppercase text-slate-400 font-semibold">Balance</p>
                            <div className="mt-0.5">
                              <AccountBalanceBadge balance={pat.account_balance} />
                            </div>
                          </div>
                          <div className="border-x border-slate-200">
                            <p className="text-[10px] uppercase text-slate-400 font-semibold">Period Visits</p>
                            <p className="text-xs font-bold text-slate-800 mt-0.5">
                              {pat.completed_in_range}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase text-slate-400 font-semibold">Period Coll.</p>
                            <p className="text-xs font-bold text-slate-900 mt-0.5 tabular-nums">
                              {inr(pat.collection_in_range)}
                            </p>
                          </div>
                        </div>

                        {/* Clinical Problem / Notes */}
                        {pat.problem && (
                          <p className="text-xs text-slate-600 line-clamp-1 italic bg-amber-50/60 px-2 py-1 rounded border border-amber-100/80">
                            Condition: {pat.problem}
                          </p>
                        )}
                      </div>

                      {/* Card Footer Actions */}
                      <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between gap-1.5">
                        {/* Direct Contact Buttons */}
                        <div className="flex items-center gap-1">
                          <a href={telLink(pat.phone)} title="Call Patient">
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-bk-blue">
                              <Phone className="h-3.5 w-3.5" />
                            </Button>
                          </a>
                          <a
                            href={waLink(pat.whatsapp || pat.phone)}
                            target="_blank"
                            rel="noreferrer"
                            title="WhatsApp Patient"
                          >
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-bk-green">
                              <MessageCircle className="h-3.5 w-3.5" />
                            </Button>
                          </a>
                          {pat.google_maps_url && (
                            <a
                              href={pat.google_maps_url}
                              target="_blank"
                              rel="noreferrer"
                              title="Google Maps Navigation"
                            >
                              <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-indigo-600">
                                <MapPin className="h-3.5 w-3.5" />
                              </Button>
                            </a>
                          )}
                        </div>

                        {/* Operational Actions */}
                        <div className="flex items-center gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedPatientForHold(pat);
                              setHoldFrom(pat.home_unavailable_from || todayIST());
                              setHoldUntil(pat.home_unavailable_until || "");
                              setHoldReason(pat.home_unavailable_reason || "");
                              setHoldNextVisit(pat.home_next_visit_date || "");
                              setHoldNotes(pat.home_unavailable_notes || "");
                              setHoldOpen(true);
                            }}
                            className="h-7 px-2 text-[11px] text-amber-700 border-amber-200 hover:bg-amber-50"
                          >
                            <Clock className="h-3 w-3 mr-1" /> {isHold ? "Edit Hold" : "Hold"}
                          </Button>

                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedPatientForSched(pat);
                              setSchedDate(todayIST());
                              setSchedSlot("");
                              setSchedNotes("");
                              setSchedOpen(true);
                            }}
                            className="h-7 px-2 text-[11px] bg-bk-green hover:bg-emerald-700 text-white"
                          >
                            <Plus className="h-3 w-3 mr-0.5" /> Visit
                          </Button>

                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/patients/${pat.id}`)}
                            className="h-7 w-7 p-0 text-slate-500 hover:text-bk-blue"
                            title="View Patient Profile"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* 1. Transfer Patient Modal */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-bk-blue">
              <ArrowRightLeft className="h-5 w-5" /> Transfer Patient to New Physiotherapist
            </DialogTitle>
          </DialogHeader>
          {selectedPatientForTransfer && (
            <div className="space-y-3 py-2">
              <div className="p-3 bg-slate-50 border rounded-lg text-xs space-y-1">
                <p className="font-bold text-slate-800">
                  {selectedPatientForTransfer.name} ({selectedPatientForTransfer.patient_id})
                </p>
                <p className="text-slate-600">
                  Current Physio: <strong>{selectedPatientForTransfer.assigned_physio_name || "Unassigned"}</strong>
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>New Physiotherapist *</Label>
                <Select value={newPhysioId} onValueChange={setNewPhysioId}>
                  <SelectTrigger><SelectValue placeholder="Select new physiotherapist" /></SelectTrigger>
                  <SelectContent>
                    {physios
                      .filter((ph) => ph.id !== selectedPatientForTransfer.assigned_physio_id)
                      .map((ph) => (
                        <SelectItem key={ph.id} value={ph.id}>
                          {ph.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Effective Transfer Date</Label>
                <Input
                  type="date"
                  value={transferDate}
                  onChange={(e) => setTransferDate(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Transfer Reason *</Label>
                <Input
                  placeholder="e.g. Locality route rebalance / Physio on leave"
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Handover Notes (Optional)</Label>
                <Textarea
                  rows={2}
                  placeholder="Clinical handover notes or patient preferences..."
                  value={transferNote}
                  onChange={(e) => setTransferNote(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button
              onClick={handleTransfer}
              disabled={transferLoading}
              className="bg-bk-blue hover:bg-blue-700 text-white"
            >
              {transferLoading ? "Transferring..." : "Confirm Transfer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2. Hold / Availability Modal */}
      <Dialog open={holdOpen} onOpenChange={setHoldOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-amber-700">
              <Clock className="h-5 w-5" /> Patient Home Service Hold / Unavailable
            </DialogTitle>
          </DialogHeader>
          {selectedPatientForHold && (
            <div className="space-y-3 py-2">
              <p className="text-xs text-slate-600">
                Patient: <strong>{selectedPatientForHold.name}</strong>. While on hold, home visits will not be scheduled and no absence penalties will occur.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Unavailable From *</Label>
                  <Input
                    type="date"
                    value={holdFrom}
                    onChange={(e) => setHoldFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Unavailable Until *</Label>
                  <Input
                    type="date"
                    value={holdUntil}
                    onChange={(e) => setHoldUntil(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Reason *</Label>
                <Input
                  placeholder="e.g. Traveling / Hospitalized / Family event"
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Next Expected Visit Date (Optional)</Label>
                <Input
                  type="date"
                  value={holdNextVisit}
                  onChange={(e) => setHoldNextVisit(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea
                  rows={2}
                  placeholder="Optional notes..."
                  value={holdNotes}
                  onChange={(e) => setHoldNotes(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between items-center">
            {selectedPatientForHold?.home_unavailable_until && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  handleClearHold(selectedPatientForHold);
                  setHoldOpen(false);
                }}
              >
                Clear Hold
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setHoldOpen(false)}>Cancel</Button>
              <Button
                onClick={handleHold}
                disabled={holdLoading}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {holdLoading ? "Saving..." : "Save Hold"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 3. Schedule Visit Modal */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-emerald-700">
              <Plus className="h-5 w-5" /> Schedule Home Visit
            </DialogTitle>
          </DialogHeader>
          {selectedPatientForSched && (
            <div className="space-y-3 py-2">
              <div className="p-3 bg-slate-50 border rounded-lg text-xs space-y-1">
                <p className="font-bold text-slate-800">{selectedPatientForSched.name}</p>
                <p className="text-slate-600">
                  Assigned Physio: <strong>{selectedPatientForSched.assigned_physio_name || "Unassigned"}</strong> · Fee: {inr(selectedPatientForSched.session_fee)}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Visit Date *</Label>
                <Input
                  type="date"
                  value={schedDate}
                  onChange={(e) => setSchedDate(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Time Slot (Optional)</Label>
                <Input
                  placeholder="e.g. 10:00 AM - 11:00 AM"
                  value={schedSlot}
                  onChange={(e) => setSchedSlot(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Visit Notes (Optional)</Label>
                <Textarea
                  rows={2}
                  placeholder="Special instructions or treatment focus..."
                  value={schedNotes}
                  onChange={(e) => setSchedNotes(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedOpen(false)}>Cancel</Button>
            <Button
              onClick={handleScheduleVisit}
              disabled={schedLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {schedLoading ? "Scheduling..." : "Schedule Visit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
